// src/pages/MatchDetails.js
import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import { getStoredUsername, saveStoredUsername } from '../../utils/usernameStorage';
import TeamImage from './TeamImage';
import ReactionPicker, { REACTIONS } from './ReactionPicker';
import UsernameModal from './UsernameModal';
import DiscussionModal from './DiscussionModal';

const MatchDetails = () => {
  const { matchId } = useParams();

  // Data
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  // Voting
  const [votingStates, setVotingStates] = useState({});
  const [voteMessages, setVoteMessages] = useState({});
  const [hasVoted, setHasVoted] = useState(false);

  // Comments
  const [comments, setComments] = useState([]);
  const [newReplies, setNewReplies] = useState({});

  // Discussions (topics)
  const [discussionResponses, setDiscussionResponses] = useState([]);
  const [showDiscussionForm, setShowDiscussionForm] = useState(false);
  const [selectedTopicIndex, setSelectedTopicIndex] = useState(-1);
  const [newResponse, setNewResponse] = useState({ topicIndex: -1, username: '', response: '' });
  const [discussionLoading, setDiscussionLoading] = useState(false);

  // Username modal
  const [storedUsername, setStoredUsername] = useState('');
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [tempUsername, setTempUsername] = useState('');

  // Timer state for live and countdown
  const [currentTime, setCurrentTime] = useState(new Date());

  // Hard cap for a live match: 3 minutes (like MatchVote page)
  const MAX_LIVE_DURATION_MS = 3 * 60 * 1000;

  // Update current time every second for live timer and countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Init
  useEffect(() => {
    checkAndLoadUsername();
    fetchMatch();
    fetchComments();
    fetchDiscussionResponses();
  }, [matchId]);

  // Username
  const checkAndLoadUsername = () => {
    const saved = getStoredUsername();
    if (saved) {
      setStoredUsername(saved);
      setNewComment((prev) => ({ ...prev, username: saved }));
      setNewResponse((prev) => ({ ...prev, username: saved }));
      setShowUsernameModal(false);
    } else {
      setShowUsernameModal(true);
    }
  };

  const [newComment, setNewComment] = useState({ username: '', comment: '' });

  const saveUsernameAndContinue = () => {
    const uname = saveStoredUsername(tempUsername);
    if (!uname) return;
    setStoredUsername(uname);
    setNewComment({ username: uname, comment: '' });
    setNewResponse({ topicIndex: -1, username: uname, response: '' });
    setShowUsernameModal(false);
    setTempUsername('');
  };

  // Fetchers
  const fetchMatch = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/matches/${matchId}`);
      if (res.ok) {
        const data = await res.json();
        setMatch(data);
      }
    } catch (e) {
      console.error('Error fetching match:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/matches/${matchId}/comments`);
      if (res.ok) {
        const data = await res.json();
        setComments(data);
      }
    } catch (e) {
      console.error('Error fetching comments:', e);
    }
  };

  const fetchDiscussionResponses = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/matches/${matchId}/discussion-responses`);
      if (res.ok) {
        const data = await res.json();
        setDiscussionResponses(data);
      }
    } catch (e) {
      console.error('Error fetching discussion responses:', e);
    }
  };

  // Helper to compute live elapsed clock string from milliseconds (from MatchVote)
  const formatElapsed = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else if (minutes > 0) {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return `0:${seconds.toString().padStart(2, '0')}`;
    }
  };

  // Calculate match elapsed time with proper formatting, capped at 3 minutes (from MatchVote)
  const getMatchElapsedTime = (startTime, status) => {
    if (status !== 'live') return null;

    const startDate = new Date(startTime);
    const currentDate = currentTime;
    const diffMs = currentDate.getTime() - startDate.getTime();

    if (diffMs <= 0) return null; // Match hasn't started yet
    if (diffMs >= MAX_LIVE_DURATION_MS) return null;
    return formatElapsed(diffMs);
  };

  // Compute derived status so an upcoming match auto-starts, becomes live, and auto-ends at 3 minutes
  const getComputedStatus = (status, startTime, endTime) => {
    const startMs = startTime ? new Date(startTime).getTime() : null;
    const nowMs = currentTime.getTime();
    const providedEndMs = endTime ? new Date(endTime).getTime() : null;
    const capEndMs = startMs !== null ? startMs + MAX_LIVE_DURATION_MS : null;

    if (status === 'ended') {
      return 'ended';
    }
    if (startMs === null) return status;
    if (nowMs < startMs) return 'upcoming';

    const effectiveEndMs =
      providedEndMs !== null && capEndMs !== null
        ? Math.min(providedEndMs, capEndMs)
        : (providedEndMs ?? capEndMs);

    if (effectiveEndMs !== null && nowMs >= effectiveEndMs) return 'ended';
    return 'live';
  };

  // Countdown (like MatchVote)
  const formatCountdown = (ms) => {
    if (ms <= 0) return null;
    const totalSec = Math.floor(ms / 1000);
    const hours = Math.floor(totalSec / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;

    if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}h left`;
    return `${minutes}:${String(seconds).padStart(2, '0')} left`;
  };

  const getTimeLeftLabel = (startTime) => {
    const s = startTime ? new Date(startTime) : null;
    if (!s) return null;
    const diff = s.getTime() - currentTime.getTime();
    if (diff <= 0) return null;
    return formatCountdown(diff);
  };

  // Voting rules
  const isVotingAllowed = () => {
  if (!match) return false;
  const computedStatus = getComputedStatus(match.status, match.startTime, match.endTime);
  if (computedStatus === 'live' || computedStatus === 'ended') return true;
  if (computedStatus === 'scheduled' || computedStatus === 'upcoming') {
    const now = new Date();
    const start = new Date(match.startTime);
    const hoursUntilStart = (start.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntilStart <= 12;  // ← CHANGED: 12 hours instead of 30 minutes
  }
  return false;
};

 const getVotingStatusMessage = () => {
  if (!match) return '';
  const computedStatus = getComputedStatus(match.status, match.startTime, match.endTime);
  if (computedStatus === 'live') return 'Vote now! Match is LIVE';
  if (computedStatus === 'ended') return 'Match ended';
  if (computedStatus === 'scheduled' || computedStatus === 'upcoming') {
    const now = new Date();
    const start = new Date(match.startTime);
    const hoursUntilStart = (start.getTime() - now.getTime()) / (1000 * 60 * 60);
    
    if (hoursUntilStart <= 12) {
      const hours = Math.floor(hoursUntilStart);
      const minutes = Math.floor((hoursUntilStart - hours) * 60);
      return `Voting open! Match starts in ${hours}h ${minutes}m`;
    }
    
    const hoursRemaining = Math.ceil(hoursUntilStart - 12);
    return `Voting opens 12 hours before kickoff (${hoursRemaining}h remaining)`;
  }
  return 'Voting not available';
};

  // Actions
  const handleVote = async (choice) => {
  if (!isVotingAllowed()) {
    setVoteMessages((prev) => ({
      ...prev,
      [matchId]: 'Voting opens 12 hours before the match starts!',
    }));

      setTimeout(() => {
        setVoteMessages((prev) => {
          const c = { ...prev };
          delete c[matchId];
          return c;
        });
      }, 3000);
      return;
    }

    setVotingStates((prev) => ({ ...prev, [matchId]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}/api/matches/${matchId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ choice }),
      });

      const data = await res.json();
      if (res.ok) {
        setVoteMessages((prev) => ({ ...prev, [matchId]: data.message || 'Vote recorded successfully!' }));
        setMatch((prev) => ({ ...prev, ...data.match }));
        setHasVoted(true); // disable further votes on UI
        setTimeout(() => {
          setVoteMessages((prev) => {
            const c = { ...prev };
            delete c[matchId];
            return c;
          });
        }, 3000);
      } else {
        if (data.message?.toLowerCase().includes('already voted')) {
          setHasVoted(true);
        }
        setVoteMessages((prev) => ({ ...prev, [matchId]: data.message || 'Unable to record vote.' }));
        setTimeout(() => {
          setVoteMessages((prev) => {
            const c = { ...prev };
            delete c[matchId];
            return c;
          });
        }, 3000);
      }
    } catch (e) {
      setVoteMessages((prev) => ({ ...prev, [matchId]: 'Network error. Please try again.' }));
    } finally {
      setVotingStates((prev) => ({ ...prev, [matchId]: false }));
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!storedUsername || !newComment.comment.trim()) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/matches/${matchId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: storedUsername, comment: newComment.comment }),
      });
      if (res.ok) {
        const comment = await res.json();
        setComments((prev) => [comment, ...prev]);
        setNewComment({ username: storedUsername, comment: '' });
      }
    } catch (e) {
      console.error('Error adding comment:', e);
    }
  };

  const handleReplySubmit = async (commentId) => {
    const content = newReplies[commentId];
    if (!content || !storedUsername) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/comments/${commentId}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: storedUsername, reply: content }),
      });
      if (res.ok) {
        const newReply = await res.json();
        setComments((prev) =>
          prev.map((c) => (c._id === commentId ? { ...c, replies: [...(c.replies || []), newReply] } : c)),
        );
        setNewReplies((prev) => ({ ...prev, [commentId]: '' }));
        setReplyStates((prev) => ({ ...prev, [commentId]: false }));
      }
    } catch (e) {
      console.error('Error adding reply:', e);
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/comments/${commentId}/like`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setComments((prev) => prev.map((c) => (c._id === commentId ? { ...c, likes: data.likes } : c)));
      }
    } catch (e) {
      console.error('Error liking comment:', e);
    }
  };

  const handleLikeDiscussionResponse = async (responseId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/discussion-responses/${responseId}/like`, {
        method: 'POST',
      });
      if (res.ok) {
        const data = await res.json();
        setDiscussionResponses((prev) =>
          prev.map((r) => (r._id === responseId ? { ...r, likes: data.likes } : r)),
        );
      }
    } catch (e) {
      console.error('Error liking discussion response:', e);
    }
  };

  // Reply helpers
  const [replyStates, setReplyStates] = useState({});
  const toggleReplyForm = (commentId) =>
    setReplyStates((prev) => ({ ...prev, [commentId]: !prev[commentId] }));
  const handleReplyChange = (commentId, value) =>
    setNewReplies((prev) => ({ ...prev, [commentId]: value }));

  // Percent helpers
  const pct = (num, den) => (den === 0 ? 0 : Math.round((num / den) * 100));

  // Vote counts and percentages (now includes draw)
  const aVotes = match?.votes?.teamA || 0;
  const bVotes = match?.votes?.teamB || 0;
  const dVotes = match?.votes?.draw || 0;
  const totalVotes = aVotes + bVotes + dVotes;
  const aPct = pct(aVotes, totalVotes);
  const bPct = pct(bVotes, totalVotes);
  const dPct = pct(dVotes, totalVotes);

  // Group responses per topic
  const groupedResponses = discussionResponses.reduce((acc, r) => {
    const k = r.topicIndex ?? -1;
    acc[k] = acc[k] || [];
    acc[k].push(r);
    return acc;
  }, {});

  // REACTIONS UI state (portal-based)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerFor, setPickerFor] = useState(null); // commentId
  const [pickerX, setPickerX] = useState(0);
  const [pickerY, setPickerY] = useState(0);
  const hoverCloseTimer = useRef(null);
  const longPressTimers = useRef({}); // map commentId -> timer id

  const placePicker = (anchorEl) => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    setPickerX(rect.left + rect.width / 2);
    setPickerY(rect.top); // above the trigger
  };

  const openPickerAt = (commentId, anchorEl) => {
    if (hoverCloseTimer.current) {
      clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
    placePicker(anchorEl);
    setPickerFor(commentId);
    setPickerOpen(true);
  };

  const closePickerDelayed = () => {
    if (hoverCloseTimer.current) clearTimeout(hoverCloseTimer.current);
    hoverCloseTimer.current = setTimeout(() => {
      setPickerOpen(false);
      setPickerFor(null);
    }, 180);
  };

  const startLongPress = (commentId, anchorEl) => {
    if (longPressTimers.current[commentId]) {
      clearTimeout(longPressTimers.current[commentId]);
    }
    longPressTimers.current[commentId] = setTimeout(() => {
      openPickerAt(commentId, anchorEl);
      longPressTimers.current[commentId] = null;
    }, 450); // long-press threshold
  };

  const cancelLongPress = async (commentId) => {
    const timer = longPressTimers.current[commentId];
    if (timer) {
      // Quick tap => default Like
      clearTimeout(timer);
      longPressTimers.current[commentId] = null;
      await handleReact(commentId, 'like');
    } else {
      // Picker already opened by long-press; do nothing here
    }
  };

  // Send reaction for a comment
  const handleReact = async (commentId, reactionType) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/comments/${commentId}/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reactionType }),
      });
      const data = await res.json();
      if (res.ok) {
        setComments((prev) =>
          prev.map((c) => (c._id === commentId ? { ...c, reactions: data.reactions } : c)),
        );
      }
    } catch (e) {
      console.error('Reaction error:', e);
    } finally {
      setPickerOpen(false);
      setPickerFor(null);
    }
  };

  // Utility: render compact reaction chips (emoji + count)
  const renderReactionsSummary = (reactions = []) => {
    if (!Array.isArray(reactions) || reactions.length === 0) return null;
    const byType = Object.fromEntries(reactions.map((r) => [r.type, r.count]));
    const items = REACTIONS.filter((r) => byType[r.key] > 0).map((r) => ({
      ...r,
      count: byType[r.key],
    }));
    if (items.length === 0) return null;
    return (
      <div className="mt-2 inline-flex items-center gap-2 bg-gray-100 rounded-full px-2 py-1">
        {items.map((item) => (
          <span key={item.key} className="text-xs flex items-center gap-1">
            <span className="select-none">{item.emoji}</span>
            <span className="text-gray-700">{item.count}</span>
          </span>
        ))}
      </div>
    );
  };

  // Close picker on scroll to avoid misplacement
  useEffect(() => {
    const onScroll = () => {
      if (pickerOpen) {
        setPickerOpen(false);
        setPickerFor(null);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pickerOpen]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="h-12 w-12 rounded-full border-2 border-b-0 border-blue-600 animate-spin" />
      </div>
    );
  }

  if (!match) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Match not found</h2>
          <Link to="/match-vote" className="text-blue-600 hover:text-blue-800">
            Back to Matches
          </Link>
        </div>
      </div>
    );
  }

  const canVote = isVotingAllowed();
  const votingStatusMessage = getVotingStatusMessage();

  // Center header fields (event name -> time -> date -> event type)
  const eventName = match.eventName || '';
  const eventType = match.eventType || '';
  const start = match.startTime ? new Date(match.startTime) : null;
  const timeText = start
    ? start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : 'TBD';
  const dateText = start
    ? start.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })
    : '';

  // Get computed status and timers
  const computedStatus = getComputedStatus(match.status, match.startTime, match.endTime);
  const elapsedTime = getMatchElapsedTime(match.startTime, computedStatus);
  const timeLeftLabel = computedStatus === 'upcoming' ? getTimeLeftLabel(match.startTime) : null;

  const handleDiscussionSubmit = async (e) => {
    e.preventDefault();
    if (!storedUsername || !newResponse.response.trim()) return;
    setDiscussionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/matches/${matchId}/discussion-responses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topicIndex: selectedTopicIndex,
          username: storedUsername,
          response: newResponse.response,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setDiscussionResponses((prev) => [data, ...prev]);
        setShowDiscussionForm(false);
        setSelectedTopicIndex(-1);
        setNewResponse({ topicIndex: -1, username: storedUsername, response: '' });
      } else {
        alert(data.message || 'Error posting response');
      }
    } catch (e) {
      alert('Network error. Please try again.');
    } finally {
      setDiscussionLoading(false);
    }
  };

  const handleDiscussionClose = () => {
    setShowDiscussionForm(false);
    setSelectedTopicIndex(-1);
    setNewResponse({ topicIndex: -1, username: storedUsername, response: '' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Username modal */}
      {showUsernameModal && (
        <UsernameModal
          tempUsername={tempUsername}
          setTempUsername={setTempUsername}
          onSave={saveUsernameAndContinue}
        />
      )}

      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Link
            to="/match-vote"
            className="inline-flex items-center border border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-800 font-medium text-sm px-4 py-2 rounded transition-all"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Matches
          </Link>
        </div>
      </div>

      {/* Card */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-white rounded-3xl shadow-sm border overflow-hidden">
          {/* Event name (top), teams row (middle), event type (bottom) */}
          <div className="px-6 sm:px-8 pt-8">
              {/* TOP: Event Name */}
            <div className="text-lg font-bold tracking-wide text-gray-600 uppercase text-center mb-2">
              {eventName || 'Event'}
            </div>

            {/* GAP + Teams row with time/date/status centered */}
            <div className="mt-6 flex items-start justify-between">
              <div className="flex flex-col items-center">
                <TeamImage logo={match.teamALogo} flag={match.teamAFlag} alt={match.teamA} className="w-16 h-16" />
                <div className="mt-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                  {match.teamA}
                </div>
              </div>

              <div className="flex-1 text-center px-2">
                {/* Time OR Live/Ended with Timer */}
                <div className="text-4xl font-extrabold">
                  {computedStatus === 'live' ? (
                    <div className="flex flex-col items-center justify-center text-red-600">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="relative flex h-4 w-4">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-4 w-4 bg-red-600"></span>
                        </span>
                        <span className="tracking-widest animate-pulse">LIVE</span>
                        <span className="relative flex h-4 w-4">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-4 w-4 bg-red-600"></span>
                        </span>
                      </div>
                      {elapsedTime && (
                        <div className="text-xl font-mono font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">
                          {elapsedTime}
                        </div>
                      )}
                    </div>
                  ) : computedStatus === 'ended' ? (
                    <div className="flex flex-col items-center justify-center text-gray-600">
                      <span className="tracking-widest">ENDED</span>
                      {/* Show start time and date under ENDED */}
                      <div className="text-sm text-gray-700 font-medium mt-2">{timeText}</div>
                      <div className="text-sm text-gray-500">{dateText}</div>
                    </div>
                  ) : (
                    <div className="text-gray-900">
                      <div>{timeText}</div>
                      <div className="text-lg text-gray-700 mt-1">{dateText || '—'}</div>
                      {/* Countdown under date for upcoming */}
                      {timeLeftLabel && (
                        <div className="mt-2 text-sm font-mono font-bold text-orange-600 bg-orange-50 inline-block px-2.5 py-1 rounded">
                          {timeLeftLabel}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-center">
                <TeamImage logo={match.teamBLogo} flag={match.teamBFlag} alt={match.teamB} className="w-16 h-16" />
                <div className="mt-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                  {match.teamB}
                </div>
              </div>
            </div>

            {/* GAP + BOTTOM: Event Type */}
            <div className="mt-8 text-base font-semibold text-gray-600 text-center">
              {eventType}
            </div>
          </div>

          {/* Vote question */}
          <div className="px-6 sm:px-8 mt-8">
            <h3 className="text-xl font-bold text-gray-900">Who will win today?</h3>

            {voteMessages[matchId] && (
              <div
                className={`mt-3 rounded-xl px-3 py-2 text-sm ${
                  voteMessages[matchId]?.toLowerCase().includes('success') || voteMessages[matchId]?.toLowerCase().includes('recorded')
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {voteMessages[matchId]}
              </div>
            )}

            {/* Three options: Team A, Team B, Draw */}
            <div className="mt-5 space-y-5">
              {/* Team A */}
              <button
                onClick={() => handleVote('teamA')}
                disabled={!canVote || votingStates[matchId] || hasVoted}
                className="w-full text-left disabled:opacity-60"
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="text-base text-gray-900">{match.teamA}</div>
                  <div className="text-base font-semibold text-gray-700">{aPct}%</div>
                </div>
                <div className="h-3.5 rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className="h-full bg-green-600 rounded-full transition-all"
                    style={{ width: `${aPct}%` }}
                  />
                </div>
              </button>

              {/* Team B */}
              <button
                onClick={() => handleVote('teamB')}
                disabled={!canVote || votingStates[matchId] || hasVoted}
                className="w-full text-left disabled:opacity-60"
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="text-base text-gray-900">{match.teamB}</div>
                  <div className="text-base font-semibold text-gray-700">{bPct}%</div>
                </div>
                <div className="h-3.5 rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className="h-full bg-indigo-600 rounded-full transition-all"
                    style={{ width: `${bPct}%` }}
                  />
                </div>
              </button>

              {/* Draw */}
              <button
                onClick={() => handleVote('draw')}
                disabled={!canVote || votingStates[matchId] || hasVoted}
                className="w-full text-left disabled:opacity-60"
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="text-base text-gray-900">Draw</div>
                  <div className="text-base font-semibold text-gray-700">{dPct}%</div>
                </div>
                <div className="h-3.5 rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className="h-full bg-amber-600 rounded-full transition-all"
                    style={{ width: `${dPct}%` }}
                  />
                </div>
              </button>

              {/* Totals (optional small note) */}
              <div className="text-xs text-gray-500">
                Total votes: {totalVotes.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Topic pills */}
          {Array.isArray(match.discussionTopics) && match.discussionTopics.length > 0 && (
            <div className="px-6 sm:px-8 mt-6">
              <div className="flex flex-wrap gap-3">
                {match.discussionTopics.map((topic, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      if (!hasVoted) return; // gated: must vote first
                      setSelectedTopicIndex(index);
                      setShowDiscussionForm(true);
                    }}
                    className={`px-4 py-2 rounded-2xl text-sm font-medium ${
                      hasVoted
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : 'bg-gray-100/60 text-gray-400 cursor-not-allowed'
                    }`}
                    title={hasVoted ? 'Respond to this topic' : 'Vote first to join the discussion'}
                  >
                    {topic}
                  </button>
                ))}
              </div>
              {!hasVoted && (
                <p className="mt-3 text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-xl px-3 py-2">
                  Vote for a team or draw first to participate in match discussions.
                </p>
              )}
            </div>
          )}

          {/* Comments */}
          <div className="px-6 sm:px-8 mt-8 pb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Comments</h3>

            {/* Add comment inline */}
            <form onSubmit={handleCommentSubmit} className="flex items-center gap-3 bg-gray-50 rounded-2xl px-4 py-3">
              <input
                type="text"
                placeholder="Add a comment..."
                value={newComment.comment}
                onChange={(e) => setNewComment((prev) => ({ ...prev, comment: e.target.value }))}
                className="flex-1 bg-transparent outline-none text-gray-900 placeholder:text-gray-400"
              />
              <button
                type="submit"
                disabled={!storedUsername || !newComment.comment.trim()}
                className="p-2 rounded-full bg-blue-600 text-white disabled:opacity-50"
                title="Send"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                  <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            </form>

            {/* Comments list */}
            <div className="mt-6 space-y-6">
              {comments.length === 0 && (
                <div className="text-gray-500 text-center py-6">No comments yet.</div>
              )}

              {comments.map((comment) => (
                <div key={comment._id} className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                    <span className="text-gray-700 font-semibold">
                      {(comment.username || '?').charAt(0).toUpperCase()}
                    </span>
                  </div>

                  {/* Body */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mr-9">
                      <div className="text-sm font-semibold text-gray-900">{comment.username}</div>
                      <div className="flex items-center gap-4">
                        {/* Like (existing) */}
                        <button
                          type="button"
                          onClick={() => handleLikeComment(comment._id)}
                          className="text-gray-500 hover:text-red-600 flex items-center gap-1"
                        >
                          <svg viewBox="0 0 24 24" className="w-4 h-4 fill-none stroke-current">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                            />
                          </svg>
                          <span className="text-sm">{comment.likes || 0}</span>
                        </button>

                        {/* React trigger (hover/long-press) */}
                        <div
                          className="relative inline-block"
                          onMouseEnter={(e) => openPickerAt(comment._id, e.currentTarget)}
                          onMouseLeave={closePickerDelayed}
                          onTouchStart={(e) => startLongPress(comment._id, e.currentTarget)}
                          onTouchEnd={() => cancelLongPress(comment._id)}
                        >
                          <button
                            type="button"
                            className="text-gray-500 hover:text-yellow-600 flex items-center gap-1"
                            title="React"
                          >
                            <span className="text-base">😊</span>
                            <span className="text-sm">React</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-800 mt-1">{comment.comment}</p>

                    {/* Aggregated reactions under the comment */}
                    {renderReactionsSummary(comment.reactions)}

                    <div className="mt-1">
                      <button
                        onClick={() => toggleReplyForm(comment._id)}
                        className="text-sm text-gray-500 hover:text-blue-600"
                      >
                        Reply
                      </button>
                    </div>

                    {/* Reply form */}
                  {/* Reply form — responsive and visible on mobile */}
{replyStates[comment._id] && (
  <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
    <input
      type="text"
      value={newReplies[comment._id] || ''}
      onChange={(e) => handleReplyChange(comment._id, e.target.value)}
      onKeyDown={(e) => e.key === 'Enter' && handleReplySubmit(comment._id)}
      className="w-full sm:flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
      placeholder="Write a reply..."
    />
    <button
      onClick={() => handleReplySubmit(comment._id)}
      className="w-full sm:w-auto px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium shadow-sm active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 shrink-0"
      disabled={!newReplies[comment._id]?.trim()}
    >
      Reply
    </button>
  </div>
)}

                    {/* Replies */}
                    {Array.isArray(comment.replies) && comment.replies.length > 0 && (
                      <div className="mt-3 pl-4 border-l-2 border-gray-100 space-y-2">
                        {comment.replies.map((reply, idx) => (
                          <div key={idx} className="text-sm">
                            <span className="font-semibold text-gray-900">{reply.username}: </span>
                            <span className="text-gray-800">{reply.reply}</span>
                            {/* Optional: show reply reactions summary if backend returns reply.reactions */}
                            {renderReactionsSummary(reply.reactions)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Global portal-based reaction picker */}
        <ReactionPicker
          open={pickerOpen}
          x={pickerX}
          y={pickerY}
          onPick={(type) => handleReact(pickerFor, type)}
          onMouseEnter={() => {
            if (hoverCloseTimer.current) {
              clearTimeout(hoverCloseTimer.current);
              hoverCloseTimer.current = null;
            }
          }}
          onMouseLeave={closePickerDelayed}
        />

        {/* Discussion response modal (opens from pills) */}
        {showDiscussionForm && selectedTopicIndex > -1 && (
          <DiscussionModal
            selectedTopicIndex={selectedTopicIndex}
            discussionTopic={match.discussionTopics?.[selectedTopicIndex]}
            storedUsername={storedUsername}
            newResponse={newResponse}
            setNewResponse={setNewResponse}
            discussionLoading={discussionLoading}
            groupedResponses={groupedResponses}
            handleLikeDiscussionResponse={handleLikeDiscussionResponse}
            onClose={handleDiscussionClose}
            onSubmit={handleDiscussionSubmit}
          />
        )}

      </div>
    </div>
  );
};

export default MatchDetails;
